import { FC } from 'react';
import {
    EditProps,
    FormWithRedirect,
    SaveButton,
    DeleteButton,
    TextInput,
    useTranslate,
    AutocompleteInput,
    SelectInput,
    required,
    ReferenceInput,
    // DateInput,
    FormDataConsumer,
    NumberInput,
    minValue,
    maxValue,
} from 'react-admin';
import {
    makeStyles,
    createStyles,
    Theme,
    Typography,
    Box,
    Toolbar,
    Grid
} from '@material-ui/core';
import getCountries from '../utils/countries'
import { getFinancingTypes, getFinancingStates } from './FinancingTypes'
import { useForm } from 'react-final-form';
import { DateInput } from '../components/DatePickers';
import { PercentFormatInput, CurrencyFormatInput, dateLocales, defaultDateOptions } from '../utils/formatters';
import _ from "lodash";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
        },
        paper: {
            padding: theme.spacing(2),
            textAlign: 'center',
            color: theme.palette.text.secondary,
        },
    }),
);

const FinancingsEdit: FC<EditProps> = props => {
    const classes = useStyles()
    const translate = useTranslate()

    const resource = props.resource

    const countries = getCountries()
    const financingTypes = getFinancingTypes()
    const financingStates = getFinancingStates()

    const listCurrencies = Object.keys(_.groupBy(countries, c => c.code)).map(k => ({ name: k, id: k }))

    return <FormWithRedirect
        {...props}
        render={formProps => (
            // here starts the custom form layout
            <Box>
                <Typography variant="h6">{translate("resources.common.fieldGroups.basicInfo")}</Typography>
                <Grid container className={classes.root} spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <ReferenceInput
                            source="merchant_id"
                            reference="Merchant"
                            sort={{ field: 'name', order: 'ASC' }}
                            perPage={10}
                            resource={resource}
                            filterToQuery={name => ({ name })}
                            validate={[required()]}
                        >
                            <AutocompleteInput optionText="name" fullWidth />
                        </ReferenceInput>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SelectInput
                            source="state"
                            resource={resource}
                            // initialValue="ACTIVE"
                            choices={financingStates}
                            fullWidth
                            validate={[required()]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SelectInput
                            source="type"
                            resource={resource}
                            // initialValue="new"
                            choices={financingTypes}
                            fullWidth
                            validate={[required()]}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SelectInput source="country" resource={resource}
                            choices={countries} fullWidth validate={[required()]} />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                        <ReferenceInput
                            source="platform_id"
                            reference="Platform"
                            resource={resource}
                            sort={{ field: 'name', order: 'ASC' }}
                            perPage={10}
                            filterToQuery={name => ({ name })}
                        >
                            <SelectInput optionText="name" fullWidth validate={[required()]} />
                        </ReferenceInput>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                        <TextInput
                            source="amount"
                            // initialValue={0}
                            resource={resource}
                            format={(v: number) => v / 100}
                            parse={(v: string) => parseFloat(v) * 100}
                            InputProps={{
                                inputComponent: CurrencyFormatInput,
                            }}
                            validate={[required()]}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3}>
                        <SelectInput source="currency"
                            defaultValue="USD"
                            choices={listCurrencies}
                            resource={resource}
                            fullWidth
                            validate={[required()]}
                        />
                    </Grid>
                    {/* <Grid item xs={12} sm={4}>
                    </Grid> */}
                    <Grid item xs={12} sm={4} lg={3} >
                        <TextInput
                            source="fixed_fee"
                            format={(v: number) => v / 100}
                            parse={(v: string) => parseFloat(v) * 100}
                            resource={resource}
                            // initialValue={20}
                            InputProps={{
                                inputComponent: PercentFormatInput,
                            }}
                            validate={[required()]}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} lg={3} >
                        <TextInput
                            source="repayment_rate"
                            format={(v: number) => v / 100}
                            // initialValue={12}
                            resource={resource}
                            parse={(v: string) => parseFloat(v) * 100}
                            InputProps={{
                                inputComponent: PercentFormatInput,
                            }}
                            validate={[required()]}
                            fullWidth
                        />
                    </Grid>
                    <FormDataConsumer>
                        {formDataProps => {
                            return <>
                                <Grid item xs={12} sm={4} lg={2} >
                                    <FinancingDateWrapper {...formDataProps} />
                                </Grid>
                                <Grid item xs={12} sm={4} lg={2} >
                                    <LimitDaysWrapper {...formDataProps} />
                                </Grid>
                                <Grid item xs={12} sm={4} lg={2} >
                                    <LimitDateWrapper {...formDataProps} />
                                </Grid>
                            </>
                        }}
                    </FormDataConsumer>
                </Grid>
                <Toolbar>
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <SaveButton
                            saving={formProps.saving}
                            handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                        />
                        <DeleteButton record={formProps.record} />
                    </Box>
                </Toolbar>
            </Box>
        )}
    />
}

const LimitDateWrapper = ({ formData, ...rest }: any) => {
    const form = useForm();

    return <DateInput
        source="limit_date"
        label="resources.Financing.fields.limit_date"
        providerOptions={{ locale: dateLocales, }}
        validate={[required(), minValue(formData.financing_date)]}
        onChange={(e: any) => {
            const end_date = e.target.valueAsDate
            const start_date = new Date(formData.financing_date)

            const daysDiff = (end_date?.getTime() - start_date?.getTime()) / (1000 * 3600 * 24)
            // console.log(daysDiff)
            form.change('limit_days', daysDiff)
        }}
        fullWidth />
}

const LimitDaysWrapper = ({ formData, ...rest }: any) => {
    const form = useForm();
    const start_date = new Date(formData.financing_date)
    const end_date = new Date(formData.limit_date)

    const daysDiff = (end_date?.getTime() - start_date?.getTime()) / (1000 * 3600 * 24)

    return <NumberInput
        {...rest}
        source="limit_days"
        label="resources.Financing.fields.limit_days"
        defaultValue={daysDiff}
        validate={[required(), minValue(0)]}
        min={0}
        onChange={(e: any) => {
            const days = parseInt(e.target.value)
            // console.log(days)
            let date = new Date(formData.financing_date)
            date.setDate(date.getDate() + days);

            form.change('limit_date', date)
        }}
        fullWidth
    />
}

const FinancingDateWrapper = ({ formData, ...rest }: any) => {
    const form = useForm();

    return <DateInput
        {...rest}
        source="financing_date"
        label="resources.Financing.fields.financing_date"
        locale={dateLocales}
        providerOptions={{ locale: dateLocales, }}
        options={defaultDateOptions}
        validate={[required(), maxValue(formData.limit_date)]}
        onChange={(e: any) => {
            const days = parseInt(formData.limit_days)
            // console.log(days)
            let date = new Date(formData.financing_date)
            date.setDate(date.getDate() + days);

            form.change('limit_date', date)
        }}
        fullWidth
    />
}

export default FinancingsEdit;
